/**
 * Shown on /login and /signup when Clerk reports an existing session, in the
 * same frame as the flow it stands in for.
 *
 * Prevents the "You're already signed in" error Clerk throws when
 * `signIn.create()` / `signUp.create()` is called with an active session —
 * common when the same identity is already signed in to the other portal on
 * the same Clerk instance. Two ways on:
 *   • Continue — to the portal's `afterSignIn` (a satellite's redirect, a
 *     pending invitation, else the dashboard).
 *   • Switch   — sign out (Clerk's default is global: every portal) and come
 *     back to this page with a fresh session.
 */
import { useAuth, useUser } from '@clerk/clerk-react';
import { LogOut } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Button } from '@tonnie/ui';
import { useCallback, useState } from 'react';
import { useLocation } from 'wouter';

import { goToDestination, useAccountPortal } from '../portal';
import { AccountAuthLayout } from './AccountAuthLayout';

export function AlreadySignedInCard({ brand }: { brand: 'sign-in' | 'sign-up' }) {
  const { t } = useTranslation();
  const [location, setLocation] = useLocation();
  const { portal, afterSignIn } = useAccountPortal();
  const { signOut } = useAuth();
  const { user } = useUser();
  const [isSwitching, setIsSwitching] = useState(false);

  const email =
    user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses[0]?.emailAddress ?? '';

  const handleSwitch = useCallback(async () => {
    setIsSwitching(true);
    try {
      await signOut();
    } finally {
      // Leave this view even if signOut throws.
      setLocation(location);
    }
  }, [signOut, setLocation, location]);

  return (
    <AccountAuthLayout
      brand={brand}
      title={t('auth.alreadySignedIn.title')}
      description={
        portal === 'business'
          ? t('auth.alreadySignedIn.descriptionBusiness', { email })
          : t('auth.alreadySignedIn.description', { email })
      }
    >
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          className="h-11 text-base font-semibold"
          onClick={() => goToDestination(afterSignIn(), setLocation)}
          disabled={isSwitching}
          data-testid="auth-already-signed-in-continue"
        >
          {t('auth.alreadySignedIn.continueToDashboard')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 gap-2 text-base font-medium"
          onClick={() => void handleSwitch()}
          disabled={isSwitching}
          data-testid="auth-already-signed-in-switch"
        >
          <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
          {isSwitching
            ? t('auth.alreadySignedIn.signingOut')
            : t('auth.alreadySignedIn.switchAccount')}
        </Button>
      </div>
    </AccountAuthLayout>
  );
}
